import LocalizedLink from "@/components/LocalizedLink";
import NextArrow from "@/components/NextArrow";
import { useLanguage } from "@/i18n/LanguageContext";

/**
 * `/community` (EN) and `/ar/community` (AR).
 *
 * Honest, on-brand placeholder for the SAS community channels until
 * they are publicly ready. The footer and the Support Center now both
 * link here so neither is left as dead text — visitors get a clear
 * "coming soon" page with concrete next steps (newsletter / direct
 * contact) instead of a 404 or a meaningless dead link.
 *
 * When the community channels go live, this page can be replaced
 * (or just rewritten) without touching any other surface.
 */

const C = {
  ar: {
    eyebrow: "المجتمع",
    title: "مجتمع SAS",
    lead:
      "نعمل حاليًا على إطلاق قنوات مجتمعية رسمية تجمع المهتمّين بالذكاء الاصطناعي في سوريا — طلابًا وباحثين ومتطوّعين. سيتم الإعلان عن طريقة الانضمام عبر صفحاتنا الرسمية بمجرد جاهزيتها.",
    untilReady: "حتى ذلك الحين:",
    bullets: [
      "تابعونا على صفحاتنا في وسائل التواصل عبر صفحة التواصل.",
      "أرسلوا طلب دعم أو سؤالًا محدّدًا عبر مركز الدعم.",
      "إذا كنتم تمثّلون جامعة أو منظّمة، تواصلوا معنا عبر نموذج الشراكة.",
    ],
    primaryCta: "اذهب إلى مركز الدعم",
    primaryTo: "/support",
    secondaryCta: "صفحة التواصل",
    secondaryTo: "/contact",
  },
  en: {
    eyebrow: "Community",
    title: "SAS Community",
    lead:
      "We are setting up official community channels for everyone interested in AI in Syria — students, researchers, and volunteers. Joining details will be announced on our official channels as soon as they are ready.",
    untilReady: "In the meantime:",
    bullets: [
      "Follow us on social channels listed on the contact page.",
      "Send a support request or a specific question through the Support Center.",
      "If you represent a university or organisation, reach us through the partnership form.",
    ],
    primaryCta: "Go to the Support Center",
    primaryTo: "/support",
    secondaryCta: "Contact page",
    secondaryTo: "/contact",
  },
};

export default function Community() {
  const { lang } = useLanguage();
  const c = C[lang];

  return (
    <section className="sas-section" data-testid="community-page">
      <div className="sas-container">
        <div className="max-w-2xl mx-auto text-center">
          <div className="sas-eyebrow mb-4" data-testid="community-eyebrow">
            {c.eyebrow}
          </div>
          <h1 className="sas-h2" data-testid="community-title">
            {c.title}
          </h1>
          <p className="sas-body mt-5 text-[16px] leading-[1.9]">{c.lead}</p>

          <div className="sas-divider my-10" />

          <p className="sas-body text-[14px] text-[color:var(--sas-ink-2)] mb-4">
            {c.untilReady}
          </p>
          <ul
            className="
              text-start max-w-xl mx-auto space-y-3 text-[15px] leading-[1.8]
              text-[color:var(--sas-ink)]
              ps-5 list-disc marker:text-[color:var(--sas-accent)]
            "
            data-testid="community-bullets"
          >
            {c.bullets.map((b, i) => (
              <li key={i}>{b}</li>
            ))}
          </ul>

          <div className="mt-10 flex flex-col sm:flex-row items-stretch sm:items-center justify-center gap-3 sm:gap-4 max-w-md mx-auto sm:max-w-none">
            <LocalizedLink
              to={c.primaryTo}
              className="sas-btn sas-btn-primary w-full sm:w-auto sm:min-w-[200px] justify-center"
              data-testid="community-cta-primary"
            >
              {c.primaryCta}
            </LocalizedLink>
            <LocalizedLink
              to={c.secondaryTo}
              className="sas-btn sas-btn-ghost w-full sm:w-auto sm:min-w-[200px] justify-center"
              data-testid="community-cta-secondary"
            >
              {c.secondaryCta} <NextArrow size={16} />
            </LocalizedLink>
          </div>
        </div>
      </div>
    </section>
  );
}
